import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useState } from "react";
import { graphql, useFragment } from "react-relay";
import { Icon } from "@/components/icon";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/utils/cn";
import type { courseSectionsPreview$key } from "./__generated__/courseSectionsPreview.graphql";
import type { courseSectionsPreview_lesson$key } from "./__generated__/courseSectionsPreview_lesson.graphql";
import type { courseSectionsPreview_section$key } from "./__generated__/courseSectionsPreview_section.graphql";

type CourseSectionsPreviewProps = {
	course: courseSectionsPreview$key;
	currentSection?: string;
	currentLessonId?: string;
};

function CourseLessonPreview({
	lesson,
	isFirst,
	isLast,
	currentLessonId,
	completedPreviousLesson,
	completedNextLesson,
}: {
	lesson: courseSectionsPreview_lesson$key;
	isFirst: boolean;
	isLast: boolean;
	currentLessonId?: string;
	completedPreviousLesson: boolean;
	completedNextLesson: boolean;
}) {
	const [isHovered, setIsHovered] = useState(false);
	const data = useFragment(
		graphql`
    fragment courseSectionsPreview_lesson on CourseClass {
      id

      slug

      name
      canAccess
      completed

      course {
        slug
      }
    }
  `,
		lesson,
	);

	const badge = () => {
		if (!data.canAccess) {
			return (
				<div className="rounded-full aspect-square flex-1 z-2 flex items-center justify-center size-4 bg-zinc-500 text-white">
					<Icon name="Lock" className="size-2" />
				</div>
			);
		}

		if (data.id === currentLessonId) {
			return (
				<div className="rounded-full aspect-square flex-1 z-2 flex items-center justify-center size-4 bg-primary">
					&nbsp;
				</div>
			);
		}

		if (data.completed) {
			return (
				<div className="rounded-full aspect-square flex-1 z-2 flex items-center justify-center size-4 bg-primary text-primary-foreground">
					<Icon name="Check" className="size-2" />
				</div>
			);
		}

		return (
			<div className="rounded-full box-border border-2 border-muted-foreground aspect-square flex-1 z-2 flex items-center justify-center size-4 bg-card">
				&nbsp;
			</div>
		);
	};

	return (
		<li className="flex h-12 flex-row items-center gap-3">
			<div className="h-full relative flex items-center justify-center">
				{badge()}

				<div className="w-0.5 absolute h-full flex flex-col">
					{isFirst ? (
						<div className="flex-1" />
					) : (
						<div
							className={cn(
								"flex-1 border-r-2 border-dashed border-muted-foreground/30 select-none",
								data.completed &&
									completedPreviousLesson &&
									"border-solid border-primary!",
							)}
						/>
					)}

					{isLast ? (
						<div className="flex-1" />
					) : (
						<div
							className={cn(
								"flex-1 border-r-2 border-dashed border-muted-foreground/30 select-none",
								data.completed && completedNextLesson
									? "border-solid border-primary!"
									: "",
							)}
						/>
					)}
				</div>
			</div>

			{data.canAccess ? (
				<Link
					to="/cursos/$course/$lesson"
					params={{ course: data.course.slug, lesson: data.slug }}
					className="flex items-center justify-start flex-1 w-fit h-fit"
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					<span
						className={cn(
							"px-2 py-0.5 rounded hover:bg-accent hover:text-accent-foreground flex flex-row items-center justify-center gap-2",
							data.id === currentLessonId && "text-primary font-medium",
						)}
					>
						<span className="flex-1 line-clamp-1">{data.name}</span>

						{isHovered && (
							<motion.div
								initial={{ opacity: 0, x: 5 }}
								animate={{ opacity: 1, x: 0 }}
							>
								<Icon name="ArrowUpRight" className="size-3" />
							</motion.div>
						)}
					</span>
				</Link>
			) : (
				<div className="h-full flex items-center justify-start flex-1">
					{data.name}
				</div>
			)}
		</li>
	);
}

function CourseSectionPreview({
	section,
	last,
	currentLessonId,
}: {
	section: courseSectionsPreview_section$key;
	last?: boolean;
	currentLessonId?: string;
}) {
	const data = useFragment(
		graphql`
    fragment courseSectionsPreview_section on CourseSection {
      id

      name

      classes(status: ACTIVE) {
        id
        name
        completed
        ...courseSectionsPreview_lesson
      }
    }
  `,
		section,
	);

	const totalLessons = data.classes.length;
	const completedLessons = data.classes.filter((c) => c.completed).length;

	return (
		<AccordionItem value={data.id}>
			<AccordionTrigger className="flex-1 w-full">
				<div className="flex flex-col">
					<span>{data.name}</span>

					<div className="flex flex-row gap-2 items-center justify-start">
						<Progress
							className="h-1 w-[30px]"
							value={(completedLessons / totalLessons) * 100}
						/>

						<span className="text-xs text-primary/60">
							{(completedLessons / totalLessons).toLocaleString("pt-BR", {
								style: "percent",
								minimumFractionDigits: 0,
								maximumFractionDigits: 1,
							})}{" "}
						</span>
					</div>
				</div>
			</AccordionTrigger>

			<AccordionContent>
				<ul className="flex flex-col">
					{data.classes.map((c, index, arr) => (
						<CourseLessonPreview
							key={c.id}
							lesson={c}
							isFirst={index === 0}
							isLast={index === data.classes.length - 1}
							currentLessonId={currentLessonId}
							completedPreviousLesson={arr[index - 1]?.completed}
							completedNextLesson={arr[index + 1]?.completed}
						/>
					))}
				</ul>
			</AccordionContent>

			{!last && <Separator />}
		</AccordionItem>
	);
}

export function CourseSectionsPreviewContent({
	course,
	currentSection,
	currentLessonId,
}: CourseSectionsPreviewProps) {
	const data = useFragment<courseSectionsPreview$key>(
		graphql`
     fragment courseSectionsPreview on Course {
       id

       sections(status: ACTIVE) {
         id
         ...courseSectionsPreview_section
       }
     }
   `,
		course,
	);

	return (
		<>
			<CardHeader>
				<CardTitle>Aulas e Módulos</CardTitle>
			</CardHeader>

			<CardContent>
				<Accordion type="single" defaultValue={currentSection}>
					{data.sections.map((d, index, arr) => (
						<CourseSectionPreview
							key={d.id}
							section={d}
							last={index === arr.length - 1}
							currentLessonId={currentLessonId}
						/>
					))}
				</Accordion>
			</CardContent>
		</>
	);
}

export function CourseSectionsPreview(props: CourseSectionsPreviewProps) {
	return (
		<Card className="max-w-[340px] relative w-full h-fit pb-0">
			<CourseSectionsPreviewContent {...props} />
		</Card>
	);
}
